import { combineReducers } from 'redux';
import aesReduxConstants from '../constants';

/**
 * auditLogReducer
 *
 * @description state of audit logs
 * - audit log list
 * - current audit log in focus, used for the audit log details page
 * - audit subjects: client, user, geodomains. typically table names
 * - audit actions: create update delete on a subject
 * @param aesReduxConstants {Object} - root redux constant values
 * @returns {ReduxReducer}
 */
const auditLogReducer = function() {
  /**
   * @interface AuditLogReducer
   */

  /**
   *  @typeof auditLogReducer
   *  @implements AuditLogReducer */

  // redux constants sub constants
  const constants = aesReduxConstants.AUDIT_LOG;

  /**
   * listReducer
   *
   * @function
   * @description returns the audit list when the redux action GET_LIST
   * is fired off and if the request fails to get the list then GET_LIST_ERROR
   * will return the defaultState of an empty array
   * @memberof AuditLogReducer
   *
   * @param state {ReduxRootState} - applications root state
   * @param action {ReduxAction} - an object that has atleast type as a key
   * @returns {Array}
   */
  const listReducer = function(state, action) {
    const defaultState = {
      loading: false,
      results: [],
      count: 0,
      limit: 10,
      pages: 0,
    };
    if (state === undefined) state = defaultState;

    switch (action.type) {
      case constants.GET_LIST:
        return Object.assign({}, state, { loading: true, results: [] });
      case constants.GET_LIST_SUCCESS:
        return Object.assign({}, action.list, { loading: false });
      case constants.GET_LIST_ERROR:
        return Object.assign({}, state, { loading: false });
      default:
        return state;
    }
  };

  const clientsReducer = function(state, action) {
    const defaultState = [];
    if (state === undefined) state = defaultState;

    switch (action.type) {
      case constants.GET_CLIENTS:
        return defaultState;
      case constants.GET_CLIENTS_SUCCESS:
        return action.clients;
      case constants.GET_CLIENTS_ERROR:
        return defaultState;
      default:
        return state;
    }
  };

  const usersReducer = function(state, action) {
    const defaultState = [];
    if (state === undefined) state = defaultState;

    switch (action.type) {
      case constants.GET_USERS:
        return defaultState;
      case constants.GET_USERS_SUCCESS:
        return action.users;
      case constants.GET_USERS_ERROR:
        return defaultState;
      default:
        return state;
    }
  };


  /**
   * currentReducer
   *
   * @function
   * @description returns an audit for the application to focus on
   * typically the returned audit is the one that will displayed in the
   * audit details pages
   * @memberof AuditLogReducer
   *
   * @param state {ReduxRootState} - applications root state
   * @param action {ReduxAction} - an object that has atleast type as a key
   * @returns {undefined}
   */
  const currentReducer = function(state, action) {
    const defaultState = {};
    if (state === undefined) state = defaultState;

    switch (action.type) {
      case constants.GET_DETAILS:
        return Object.assign({}, state, { loading: true });
      case constants.GET_DETAILS_SUCCESS:
        return Object.assign({}, action.current, { loading: false });
      case constants.GET_DETAILS_ERROR:
        return Object.assign({}, state, { loading: false });
      default:
        return state;
    }
  };

  const relatedReducer = function(state, action) {
    const defaultState = {
      before: [],
      after: [],
    };
    if (state === undefined) state = defaultState;

    switch (action.type) {
      case constants.GET_RELATED:
        return defaultState;
      case constants.GET_RELATED_SUCCESS:
        return action.related;
      case constants.GET_RELATED_ERROR:
        return defaultState;
      default:
        return state;
    }
  };

  /**
   * subjectsReducer
   *
   * @param state
   * @param action
   * @returns {undefined}
   */
  const subjectsReducer = function(state, action) {
    const defaultState = [];
    if (state === undefined) state = defaultState;

    switch (action.type) {
      case constants.GET_SUBJECTS:
        return defaultState;
      case constants.GET_SUBJECTS_SUCCESS:
        return action.subjects;
      case constants.GET_SUBJECTS_ERROR:
        return defaultState;
      default:
        return state;
    }
  };

  const actionsReducer = function(state, action) {
    const defaultState = [];
    if (state === undefined) state = defaultState;

    switch (action.type) {
      case constants.GET_ACTIONS:
        return defaultState;
      case constants.GET_ACTIONS_SUCCESS:
        return action.actions;
      case constants.GET_ACTIONS_ERROR:
        return defaultState;
      default:
        return state;
    }
  };


  return combineReducers({
    list: listReducer,
    current: currentReducer,
    related: relatedReducer,
    clients: clientsReducer,
    users: usersReducer,
    subjects: subjectsReducer,
    actions: actionsReducer,
  });
};

export default auditLogReducer();
